import { formatarDataBr, dataParaInput } from '@/utils/date';

/** Chave localStorage para sprints fixadas no painel de gestão */
export const STORAGE_SPRINTS_FIXADAS = 'gestao-sprints-fixadas';

/**
 * Segunda e sexta-feira da semana atual (YYYY-MM-DD).
 */
export function getSegundaASextaSemanaAtual() {
  const today = new Date();
  const day = today.getDay();
  const diffToMonday = today.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(today);
  monday.setDate(diffToMonday);
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4);

  return {
    inicio: formatarDataLocal(monday),
    fim: formatarDataLocal(friday),
  };
}

function formatarDataLocal(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Desloca um período segunda–sexta em N semanas (negativo = anterior). */
export function deslocarSemana(inicio, fim, semanas) {
  const dInicio = parseDataLocal(inicio);
  const dFim = parseDataLocal(fim);
  dInicio.setDate(dInicio.getDate() + semanas * 7);
  dFim.setDate(dFim.getDate() + semanas * 7);
  return {
    inicio: formatarDataLocal(dInicio),
    fim: formatarDataLocal(dFim),
  };
}

export function isSemanaAtual(inicio, fim) {
  const atual = getSegundaASextaSemanaAtual();
  return inicio === atual.inicio && fim === atual.fim;
}

/** Rótulo legível do período, ex.: "19/05/2026 – 23/05/2026". */
export function labelPeriodoSemana(inicio, fim) {
  if (!inicio || !fim) return '';
  const formatar = (d) => {
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y}`;
  };
  return `${formatar(inicio)} – ${formatar(fim)}`;
}

function parseDataLocal(isoDate) {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Datas previstas da sprint (dtInicio/dtTermino) ou segunda–sexta da semana atual. */
export function datasPrevistasDaSprint(sprint) {
  const inicio = sprint?.dtInicio ? String(sprint.dtInicio).slice(0, 10) : null;
  const fim = sprint?.dtTermino ? String(sprint.dtTermino).slice(0, 10) : null;
  if (inicio && fim) return { inicio, fim };
  return getSegundaASextaSemanaAtual();
}

export function carregarSprintsFixadas() {
  try {
    const raw = localStorage.getItem(STORAGE_SPRINTS_FIXADAS);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(Number).filter(Boolean) : [];
  } catch {
    return [];
  }
}

export function salvarSprintsFixadas(ids) {
  localStorage.setItem(STORAGE_SPRINTS_FIXADAS, JSON.stringify(ids));
}

export function chaveStorageEntregasFixadas(userId) {
  const id = Number(userId);
  if (!Number.isFinite(id) || id <= 0) return 'gestao-entregas-fixadas-anon';
  return `gestao-entregas-fixadas-${id}`;
}

export function carregarEntregasFixadas(userId) {
  try {
    const raw = localStorage.getItem(chaveStorageEntregasFixadas(userId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? [...new Set(parsed.map(Number).filter(Boolean))] : [];
  } catch {
    return [];
  }
}

export function salvarEntregasFixadas(userId, ids) {
  localStorage.setItem(chaveStorageEntregasFixadas(userId), JSON.stringify(ids));
}

/**
 * Julgamento do estado da sprint com base nas tarefas e no fim previsto.
 *
 * alerta (vermelho): tarefas atrasadas, sprint vencida com pendências, ou >25% das tarefas ativas atrasadas.
 * aviso (amarelo): vencimento em até 2 dias com baixo avanço, sprint sem datas nas tarefas, ou <50% concluído com fim em ≤3 dias.
 * ok (verde): demais casos em dia.
 */
export function calcularEstadoSprint(sprint, hoje = new Date().toISOString().slice(0, 10)) {
  const avisos = sprint.avisos || [];
  if (avisos.some((a) => a.nivel === 'alerta')) return 'alerta';

  const resumo = sprint.resumo_tarefas || {};
  const { total = 0, atrasadas = 0, sem_data: semData = 0, percentual_concluido: pct = 0 } = resumo;
  const ativas = total;
  const fimPrevisto = sprint.fim_previsto;

  if (atrasadas > 0) return 'alerta';

  if (avisos.some((a) => a.nivel === 'aviso')) return 'aviso';

  if (fimPrevisto && fimPrevisto < hoje && ativas > 0 && resumo.concluido < total) {
    return 'alerta';
  }

  if (semData > 0 && ativas > 0 && semData === ativas) {
    return 'aviso';
  }

  if (fimPrevisto) {
    const diasParaFim = diasEntre(hoje, fimPrevisto);
    if (diasParaFim >= 0 && diasParaFim <= 2 && pct < 70) return 'aviso';
    if (diasParaFim >= 0 && diasParaFim <= 3 && pct < 50 && ativas > 0) return 'aviso';
  }

  if (sprint.status_sprint === 'andamento' && pct < 30 && ativas >= 3) {
    return 'aviso';
  }

  if (avisos.length > 0) return 'aviso';

  return 'ok';
}

/** Prioridade para ordenação: crítico → atenção → em dia. */
export const ORDEM_ESTADO_SPRINT = {
  alerta: 0,
  aviso: 1,
  ok: 2,
};

/** Compara sprints para exibição: estado, fixada, fim previsto. */
export function compararSprintsGestao(a, b) {
  const pesoA = ORDEM_ESTADO_SPRINT[calcularEstadoSprint(a)] ?? 2;
  const pesoB = ORDEM_ESTADO_SPRINT[calcularEstadoSprint(b)] ?? 2;
  if (pesoA !== pesoB) return pesoA - pesoB;

  if (Boolean(a.fixada) !== Boolean(b.fixada)) {
    return Number(b.fixada) - Number(a.fixada);
  }

  return String(a.fim_previsto ?? '').localeCompare(String(b.fim_previsto ?? ''));
}

/** Percentual mínimo de tarefas concluídas para considerar a entrega em dia perto do fim (alinhado ao backend). */
export const PERCENTUAL_TAREFAS_EM_DIA_PERTO_FIM = 70;

/** Tipos de aviso que não alteram o indicador Em dia / Atenção / Crítico. */
const TIPOS_AVISO_IGNORADOS_ESTADO_ENTREGA = new Set(['aguardando_validacao']);

/** Avisos considerados no cálculo do estado da entrega (exclui informativos e validação pendente). */
export function avisosQueAfetamEstadoEntrega(avisos = []) {
  return (avisos || []).filter(
    (a) => !TIPOS_AVISO_IGNORADOS_ESTADO_ENTREGA.has(a.tipo) && a.nivel !== 'info',
  );
}

/**
 * Julgamento do estado da entrega com base nos avisos e no fim previsto.
 *
 * alerta (vermelho): alertas críticos ou fim previsto vencido sem conclusão.
 * aviso (amarelo): alertas de atenção (ex.: prazo próximo com baixo avanço nas tarefas).
 * ok (verde): sem alertas críticos/de atenção; concluída aguardando validação conta como em dia.
 */
export function calcularEstadoEntrega(entrega, hoje = new Date().toISOString().slice(0, 10)) {
  const status = entrega.entrega_status;

  // Concluída ou cancelada: encerrada, não entra em alerta de prazo.
  if (status === 'Concluído' || status === 'Cancelada') {
    return 'ok';
  }

  const avisos = avisosQueAfetamEstadoEntrega(entrega.avisos);
  if (avisos.some((a) => a.nivel === 'alerta')) return 'alerta';

  const fimPrevisto = entrega.fim_previsto;

  if (fimPrevisto && fimPrevisto < hoje) {
    return 'alerta';
  }

  if (avisos.some((a) => a.nivel === 'aviso')) return 'aviso';

  return 'ok';
}

/** Compara entregas sem fim previsto: projeto, depois nome da entrega. */
export function compararEntregasSemFimGestao(a, b) {
  const porProjeto = String(a.projeto_nome ?? '').localeCompare(String(b.projeto_nome ?? ''));
  if (porProjeto !== 0) return porProjeto;

  return String(a.entrega_nome ?? '').localeCompare(String(b.entrega_nome ?? ''));
}

/** Compara entregas para exibição: estado, fim previsto. */
export function compararEntregasGestao(a, b) {
  const pesoA = ORDEM_ESTADO_SPRINT[calcularEstadoEntrega(a)] ?? 2;
  const pesoB = ORDEM_ESTADO_SPRINT[calcularEstadoEntrega(b)] ?? 2;
  if (pesoA !== pesoB) return pesoA - pesoB;

  return String(a.fim_previsto ?? '').localeCompare(String(b.fim_previsto ?? ''));
}

export const ICONE_AVISO_NIVEL = {
  alerta: 'bi-exclamation-octagon-fill',
  aviso: 'bi-exclamation-triangle-fill',
  info: 'bi-info-circle-fill',
};

/** Nível mais grave entre os avisos (alerta > aviso > info). */
export function nivelMaximoAvisos(avisos) {
  if (!avisos?.length) return null;
  if (avisos.some((a) => a.nivel === 'alerta')) return 'alerta';
  if (avisos.some((a) => a.nivel === 'aviso')) return 'aviso';
  return 'info';
}

function diasEntre(dataA, dataB) {
  const a = new Date(dataA + 'T12:00:00');
  const b = new Date(dataB + 'T12:00:00');
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

export const ESTADO_SPRINT_LABEL = {
  ok: 'Em dia',
  aviso: 'Atenção',
  alerta: 'Crítico',
};

export const ESTADO_SPRINT_ICONE = {
  ok: 'bi-check-circle-fill',
  aviso: 'bi-exclamation-triangle-fill',
  alerta: 'bi-exclamation-octagon-fill',
};

/** Critérios exibidos ao passar o mouse sobre o indicador de estado da sprint. */
export const ESTADO_SPRINT_CRITERIOS = {
  alerta: {
    titulo: 'Crítico',
    itens: [
      'Há tarefas atrasadas (fim previsto anterior a hoje e não concluídas)',
      'O prazo da sprint já passou e ainda existem tarefas em aberto',
      'A sprint está concluída, mas há tarefas pendentes ou em andamento',
      'Há tarefas com início previsto posterior ao fim previsto',
      'Existe algum alerta da sprint classificado como crítico',
    ],
  },
  aviso: {
    titulo: 'Atenção',
    itens: [
      'Sprint sem fim previsto definido, sem tarefas ou com alertas de qualidade de dados',
      'Tarefas ativas sem responsável ou sem datas de início/fim previstas',
      'Datas de tarefas fora do período da sprint',
      'Faltam até 2 dias para o fim e menos de 70% das tarefas concluídas',
      'Faltam até 3 dias para o fim e menos de 50% concluídas',
      'Sprint em andamento com menos de 30% concluído e 3 ou mais tarefas',
      'Todas as tarefas ativas estão sem datas planejadas',
    ],
  },
  ok: {
    titulo: 'Em dia',
    itens: [
      'Nenhuma tarefa atrasada e nenhum alerta crítico',
      'O prazo da sprint não venceu com tarefas relevantes em aberto',
      'Avanço e prazos dentro dos limites descritos em Atenção e Crítico',
    ],
  },
};

/** Critérios exibidos no modal do indicador de estado da entrega (gestão de entregas). */
export const ESTADO_ENTREGA_CRITERIOS = {
  alerta: {
    titulo: 'Crítico',
    itens: [
      'Pacote de trabalho em atraso em relação ao fim previsto',
      'Existem tarefas atrasadas no pacote de trabalho',
      'Há tarefas em aberto em sprint com prazo encerrado ou sprint finalizada',
      'Há tarefas com início previsto posterior ao fim previsto',
    ],
  },
  aviso: {
    titulo: 'Atenção',
    itens: [
      'Pacote de trabalho sem início ou fim previsto, ou sem coordenador',
      'Pacote de trabalho sem sprint, sem sprint em andamento ou sem sprint com fim no período',
      'Pacote de trabalho sem tarefas nas sprints',
      'Tarefas ativas sem responsável ou sem datas previstas',
      'Faltam até 3 dias para o fim previsto e menos de 70% das tarefas concluídas (ou sem tarefas cadastradas)',
      'Avisos informativos (ex.: prazo em 4–7 dias, ou prazo próximo com avanço ≥ 70%)',
    ],
  },
  ok: {
    titulo: 'Em dia',
    itens: [
      'Nenhum alerta crítico ou de atenção do pacote de trabalho',
      'O fim previsto do pacote de trabalho não está vencido',
      'Prazo próximo (até 3 dias) com pelo menos 70% das tarefas concluídas',
      'Pacote de trabalho concluído aguardando validação da gestão (não altera este indicador)',
      'Planejamento e prazos dentro dos limites de Atenção e Crítico',
    ],
  },
};

export const ESTADOS_SPRINT_REFERENCIA = ['ok', 'aviso', 'alerta'];

/** Estados de referência para a legenda do painel de gestão de entregas. */
export const ESTADOS_ENTREGA_REFERENCIA = ESTADOS_SPRINT_REFERENCIA;

export const STATUS_SPRINT_LABEL = {
  pendente: 'Não iniciada',
  andamento: 'Em andamento',
  concluida: 'Concluída',
};

export function labelStatusEntrega(status) {
  if (!status) return '—';
  if (status === 'Concluído') return 'Concluída';
  if (status === 'Cancelada') return 'Cancelada';
  if (String(status).startsWith('Em andamento')) return status;
  if (status === 'Pendente') return 'Pendente';
  return status;
}

const PREFIXO_LINHA_REPACTUACAO = /^Fim previsto alterado de .+ para .+ pelo seguinte motivo: /;

/** Texto padrão da nova linha de repactuação (usuário completa após os dois pontos). */
export function montarPrefixoLinhaRepactuacao(fimDe, fimPara) {
  const deFmt = fimDe ? formatarDataBr(fimDe, 'sem data prevista') : 'sem data prevista';
  const paraFmt = formatarDataBr(fimPara);
  if (!paraFmt || paraFmt === '-') return '';
  return `Fim previsto alterado de ${deFmt} para ${paraFmt} pelo seguinte motivo: `;
}

/** Separa o histórico salvo da linha nova em edição pendente. */
export function extrairLinhaNovaRepactuacao(historico, textoCompleto) {
  const full = String(textoCompleto || '').trim();
  if (!full) return '';
  const hist = String(historico || '').trim();
  if (!hist) return full;
  if (full === hist) return '';
  if (full.startsWith(hist)) {
    return full.slice(hist.length).replace(/^\n+/, '');
  }
  return full;
}

/** Concatena histórico com a nova linha de repactuação. */
export function juntarObservacaoRepactuacao(historico, linhaNova) {
  const partes = [String(historico || '').trim(), String(linhaNova || '').trim()].filter(Boolean);
  return partes.join('\n');
}

export function dataRepactuacaoAlterada(fimReferencia, fimSelecionado) {
  const ref = dataParaInput(fimReferencia);
  const atual = dataParaInput(fimSelecionado);
  if (!atual) return false;
  return atual !== ref;
}

export function motivoRepactuacaoPreenchido(linhaNova, prefixo) {
  const linha = String(linhaNova || '');
  const pref = String(prefixo || '');
  if (!pref || !linha.startsWith(pref)) return false;
  return Boolean(linha.slice(pref.length).trim());
}

/** Status de projeto que não entram nos blocos do painel (exceto entregas fixadas). */
export const STATUS_PROJETO_EXCLUIDOS_GESTAO_ENTREGAS = [
  'Cancelado',
  'Reprovado',
  'Proposto',
  'Suspenso',
  'Concluído',
];

export function projetoStatusExcluidoGestaoEntregas(status) {
  return STATUS_PROJETO_EXCLUIDOS_GESTAO_ENTREGAS.includes(status);
}

export function usuarioEnvolvidoNaEntrega(entrega, idUsuario) {
  const id = Number(idUsuario);
  if (!Number.isFinite(id) || id <= 0) return false;
  return Number(entrega?.gerente_id) === id || Number(entrega?.coordenador_id) === id;
}

/**
 * @param {Array} lista
 * @param {{ somenteEstrategico?: boolean, incluirStatusExcluidos?: boolean, idUsuario?: number|null, isAuditor?: boolean }} opcoes
 */
export function filtrarEntregasGestao(lista, opcoes = {}) {
  const {
    somenteEstrategico = false,
    incluirStatusExcluidos = false,
    idUsuario = null,
    isAuditor = true,
  } = opcoes;
  let result = Array.isArray(lista) ? lista : [];
  if (somenteEstrategico) {
    result = result.filter((e) => e.projeto_estrategico);
  }
  if (!incluirStatusExcluidos) {
    result = result.filter((e) => !projetoStatusExcluidoGestaoEntregas(e.projeto_status));
  }
  if (!isAuditor) {
    result = result.filter((e) => usuarioEnvolvidoNaEntrega(e, idUsuario));
  }
  return result;
}

export { PREFIXO_LINHA_REPACTUACAO };
